<template>
  <div
    class="drawer-overlay"
    :class="{ visible }"
    @click.self="close"
  >
    <transition name="slide-left">

      <div
        v-if="visible"
        class="drawer-content"
      >

        <!-- =====================================================
             HEADER
        ====================================================== -->

        <div class="drawer-header">

          <div class="drawer-header-text">

            <h2 class="drawer-title">
              Добавить язык
            </h2>

            <p class="drawer-subtitle">
              Выберите язык и укажите уровень владения
            </p>

          </div>


          <button
            class="close-button"
            type="button"
            :disabled="isSaving"
            aria-label="Закрыть"
            @click="close"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5.5 5.5L14.5 14.5M14.5 5.5L5.5 14.5"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </button>

        </div>


        <!-- =====================================================
             BODY
        ====================================================== -->

        <div class="drawer-body">

          <div
            class="form-content"
            :class="{
              'form-content--disabled': isSaving
            }"
          >

            <!-- =================================================
                 LANGUAGE
            ================================================== -->

            <section class="form-section">

              <div class="section-heading">

                <div class="section-number">
                  01
                </div>

                <div>

                  <h3 class="section-title">
                    Язык
                  </h3>

                  <p class="section-description">
                    Выберите язык, который хотите добавить в профиль
                  </p>

                </div>

              </div>


              <div
                v-if="availableLanguages.length"
                class="languages-grid"
              >

                <button
                  v-for="language in availableLanguages"
                  :key="language"
                  type="button"
                  class="language-card"
                  :class="{
                    selected:
                      selectedLanguage === language
                  }"
                  :disabled="isSaving"
                  @click="selectLanguage(language)"
                >

                  <div class="language-icon">
                    {{ getLanguageCode(language) }}
                  </div>


                  <span class="language-name">
                    {{ language }}
                  </span>


                  <div class="selection-mark">

                    <svg
                      v-if="selectedLanguage === language"
                      viewBox="0 0 18 18"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M4.5 9.2L7.5 12L13.5 6"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>

                  </div>

                </button>

              </div>


              <div
                v-else
                class="empty-languages"
              >

                <div class="empty-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M4 5.5H20M8 3.5V5.5M16 3.5V5.5M6.5 9.5H17.5M6.5 13H13.5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />

                    <rect
                      x="3.5"
                      y="5.5"
                      width="17"
                      height="15"
                      rx="4"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                  </svg>
                </div>


                <div>
                  <div class="empty-title">
                    Все языки уже добавлены
                  </div>

                  <div class="empty-description">
                    Доступных языков для добавления больше нет
                  </div>
                </div>

              </div>


              <p
                v-if="errors.language"
                class="field-error"
              >
                {{ errors.language }}
              </p>

            </section>


            <div class="section-divider"></div>


            <!-- =================================================
                 LEVEL
            ================================================== -->

            <section class="form-section">

              <div class="section-heading">

                <div class="section-number">
                  02
                </div>

                <div>

                  <h3 class="section-title">
                    Уровень владения
                  </h3>

                  <p class="section-description">
                    Выберите вариант, который лучше всего отражает ваши знания
                  </p>

                </div>

              </div>


              <div class="levels-list">

                <button
                  v-for="level in levelOptions"
                  :key="level.value"
                  type="button"
                  class="level-card"
                  :class="{
                    selected:
                      selectedLevel === level.value
                  }"
                  :disabled="isSaving"
                  @click="selectLevel(level.value)"
                >

                  <div class="level-main">

                    <div
                      class="level-indicator"
                      :class="{
                        selected:
                          selectedLevel === level.value
                      }"
                    >
                      <div class="level-indicator-dot"></div>
                    </div>


                    <div class="level-copy">

                      <div class="level-name">
                        {{ level.value }}
                      </div>

                      <div class="level-description">
                        {{ level.description }}
                      </div>

                    </div>

                  </div>


                  <div class="level-bars">

                    <span
                      v-for="bar in 4"
                      :key="bar"
                      :class="{
                        filled:
                          bar <= level.strength
                      }"
                    ></span>

                  </div>

                </button>

              </div>


              <p
                v-if="errors.level"
                class="field-error"
              >
                {{ errors.level }}
              </p>

            </section>


            <!-- =================================================
                 SUMMARY
            ================================================== -->

            <transition name="summary-fade">

              <div
                v-if="
                  selectedLanguage &&
                  selectedLevel
                "
                class="selection-summary"
              >

                <div class="summary-icon">

                  <svg
                    viewBox="0 0 22 22"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="11"
                      cy="11"
                      r="8"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />

                    <path
                      d="M7.5 11L9.8 13.3L14.7 8.5"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                </div>


                <div class="summary-copy">

                  <div class="summary-title">
                    Готово к добавлению
                  </div>

                  <div class="summary-text">
                    {{ selectedLanguage }}
                    <span>·</span>
                    {{ selectedLevel }}
                  </div>

                </div>

              </div>

            </transition>

          </div>


          <!-- =====================================================
               SAVING
          ====================================================== -->

          <transition name="fade">

            <div
              v-if="isSaving"
              class="saving-overlay"
            >

              <div class="saving-card">

                <div class="saving-spinner"></div>


                <div class="saving-text">

                  <strong>
                    Сохраняем язык
                  </strong>

                  <span>
                    Это займёт несколько секунд
                  </span>

                </div>

              </div>

            </div>

          </transition>

        </div>


        <!-- =====================================================
             FOOTER
        ====================================================== -->

        <div class="drawer-footer">

          <button
            class="cancel-button"
            type="button"
            :disabled="isSaving"
            @click="close"
          >
            Отмена
          </button>


          <button
            class="save-btn"
            type="button"
            :disabled="
              !isFormValid ||
              isSaving
            "
            @click="saveLanguage"
          >

            <template v-if="isSaving">

              <span class="button-spinner"></span>

              <span>
                Сохраняем...
              </span>

            </template>


            <template v-else>

              <svg
                viewBox="0 0 18 18"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 9.1L7.1 12.1L14 5.5"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <span>
                Сохранить
              </span>

            </template>

          </button>

        </div>

      </div>

    </transition>
  </div>
</template>


<script setup>

import {
  computed,
  ref,
  watch
} from "vue";

import SummaryService
  from "../../api/SummaryService";


/* ============================================================
   PROPS
============================================================ */

const props =
  defineProps({

    visible: {
      type: Boolean,
      default: false
    },

    existingLanguages: {
      type: Array,
      default: () => []
    }

  });


/* ============================================================
   EMITS
============================================================ */

const emit =
  defineEmits([
    "close",
    "save",
    "snackbar"
  ]);


/* ============================================================
   STATE
============================================================ */

const isSaving =
  ref(false);

const selectedLanguage =
  ref("");

const selectedLevel =
  ref("");


const errors =
  ref({

    language:
      "",

    level:
      ""

  });


/* ============================================================
   DATA
============================================================ */

const languages = [

  "Английский",
  "Немецкий",
  "Французский",
  "Испанский",
  "Китайский",
  "Японский",
  "Итальянский",
  "Португальский"

];


const levelOptions = [

  {
    value:
      "Начальный",

    description:
      "Понимаю простые фразы и могу поддержать базовый диалог",

    strength:
      1
  },

  {
    value:
      "Средний",

    description:
      "Уверенно общаюсь на повседневные и рабочие темы",

    strength:
      2
  },

  {
    value:
      "Продвинутый",

    description:
      "Свободно использую язык в работе и сложном общении",

    strength:
      3
  },

  {
    value:
      "Носитель",

    description:
      "Владею языком на уровне родного",

    strength:
      4
  }

];


const levels =
  levelOptions.map(
    item =>
      item.value
  );


/* ============================================================
   EXISTING LANGUAGES
============================================================ */

const existingLanguageNames =
  computed(() => {

    return props.existingLanguages

      .map(
        item => {

          if (
            typeof item === "string"
          ) {

            return item
              .trim()
              .toLowerCase();

          }


          return String(
            item?.language ||
            ""
          )
            .trim()
            .toLowerCase();

        }
      )

      .filter(Boolean);

  });


const availableLanguages =
  computed(() => {

    return languages.filter(
      language => {

        return (
          !existingLanguageNames.value
            .includes(
              language.toLowerCase()
            )
        );

      }
    );

  });


/* ============================================================
   DUPLICATE
============================================================ */

const isDuplicateLanguage =
  computed(() => {

    if (
      !selectedLanguage.value
    ) {
      return false;
    }


    return existingLanguageNames.value
      .includes(
        selectedLanguage.value
          .toLowerCase()
      );

  });


/* ============================================================
   FORM VALIDITY
============================================================ */

const isFormValid =
  computed(() => {

    return Boolean(

      selectedLanguage.value &&

      selectedLevel.value &&

      !isDuplicateLanguage.value

    );

  });


/* ============================================================
   WATCH
============================================================ */

watch(
  () => props.visible,
  visible => {

    if (visible) {
      clearErrors();
    }

  }
);


/* ============================================================
   SELECT LANGUAGE
============================================================ */

function selectLanguage(
  language
) {

  if (
    isSaving.value
  ) {
    return;
  }


  selectedLanguage.value =
    language;


  clearError(
    "language"
  );

}


/* ============================================================
   SELECT LEVEL
============================================================ */

function selectLevel(
  level
) {

  if (
    isSaving.value
  ) {
    return;
  }


  selectedLevel.value =
    level;


  clearError(
    "level"
  );

}


/* ============================================================
   LANGUAGE CODE
============================================================ */

function getLanguageCode(
  language
) {

  const codes = {

    Английский:
      "EN",

    Немецкий:
      "DE",

    Французский:
      "FR",

    Испанский:
      "ES",

    Китайский:
      "ZH",

    Японский:
      "JA",

    Итальянский:
      "IT",

    Португальский:
      "PT"

  };


  return codes[language] ||
    language
      .slice(0, 2)
      .toUpperCase();

}


/* ============================================================
   ERRORS
============================================================ */

function clearErrors() {

  errors.value = {

    language:
      "",

    level:
      ""

  };

}


function clearError(
  field
) {

  errors.value[field] =
    "";

}


/* ============================================================
   RESET
============================================================ */

function resetForm() {

  selectedLanguage.value =
    "";

  selectedLevel.value =
    "";

  clearErrors();

}


/* ============================================================
   VALIDATION
============================================================ */

function validateLanguage() {

  if (
    !selectedLanguage.value
  ) {

    errors.value.language =
      "Выберите язык";

    return false;

  }


  if (
    isDuplicateLanguage.value
  ) {

    errors.value.language =
      "Этот язык уже добавлен";

    return false;

  }


  errors.value.language =
    "";

  return true;

}


function validateLevel() {

  if (
    !selectedLevel.value
  ) {

    errors.value.level =
      "Выберите уровень владения";

    return false;

  }


  if (
    !levels.includes(
      selectedLevel.value
    )
  ) {

    errors.value.level =
      "Выберите корректный уровень";

    return false;

  }


  errors.value.level =
    "";

  return true;

}


function validateForm() {

  const languageValid =
    validateLanguage();

  const levelValid =
    validateLevel();


  return (
    languageValid &&
    levelValid
  );

}


/* ============================================================
   CLOSE
============================================================ */

function close() {

  if (
    isSaving.value
  ) {
    return;
  }


  resetForm();


  emit(
    "close"
  );

}


/* ============================================================
   SAVE
============================================================ */

async function saveLanguage() {

  if (
    isSaving.value
  ) {
    return;
  }


  if (
    !validateForm()
  ) {

    emit(
      "snackbar",
      {

        message:
          "Проверьте заполненные поля",

        type:
          "error"

      }
    );


    return;

  }


  const languageData = {

    language:
      selectedLanguage.value,

    language_level:
      selectedLevel.value

  };


  const payload = {

    language:
      languageData

  };


  isSaving.value =
    true;


  try {

    const response =
      await SummaryService
        .addLanguage(
          payload
        );


    const responseLanguage =

      response?.language ||

      response?.data?.language ||

      response?.data ||

      response ||

      {};


    const savedLanguage = {

      ...languageData,

      ...responseLanguage

    };


    console.log(
      "✅ Язык успешно добавлен:",
      savedLanguage
    );


    emit(
      "save",
      savedLanguage
    );


    emit(
      "snackbar",
      {

        message:
          "Язык успешно добавлен",

        type:
          "success"

      }
    );


    resetForm();


    emit(
      "close"
    );

  } catch (error) {

    console.error(
      "❌ Ошибка при сохранении языка:",
      error
    );


    const errorMessage =

      error?.response?.data?.message ||

      error?.message ||

      "Не удалось сохранить язык";


    emit(
      "snackbar",
      {

        message:
          errorMessage,

        type:
          "error"

      }
    );

  } finally {

    isSaving.value =
      false;

  }

}

</script>


<style scoped>

/* ============================================================
   OVERLAY
============================================================ */

.drawer-overlay {
  position:
    fixed;

  inset:
    0;

  z-index:
    1000;

  display:
    flex;

  align-items:
    stretch;

  justify-content:
    flex-start;

  background:
    rgba(17, 21, 28, .34);

  backdrop-filter:
    blur(2px);

  -webkit-backdrop-filter:
    blur(2px);

  opacity:
    0;

  pointer-events:
    none;

  transition:
    opacity .24s ease;
}


.drawer-overlay.visible {
  opacity:
    1;

  pointer-events:
    auto;
}


/* ============================================================
   DRAWER
============================================================ */

.drawer-content {
  width:
    min(
      720px,
      100%
    );

  height:
    100%;

  display:
    flex;

  flex-direction:
    column;

  box-sizing:
    border-box;

  overflow:
    hidden;

  background:
    #fbfbfc;

  border-right:
    1px solid
    rgba(28, 38, 55, .06);

  box-shadow:
    24px
    0
    70px
    rgba(20, 30, 48, .16);
}


/* ============================================================
   HEADER
============================================================ */

.drawer-header {
  min-height:
    96px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    space-between;

  gap:
    24px;

  box-sizing:
    border-box;

  padding:
    22px
    30px;

  background:
    rgba(255, 255, 255, .86);

  border-bottom:
    1px solid
    rgba(25, 35, 50, .06);

  backdrop-filter:
    blur(16px);

  -webkit-backdrop-filter:
    blur(16px);
}


.drawer-header-text {
  min-width:
    0;
}


.drawer-title {
  margin:
    0;

  color:
    #30343b;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    19px;

  line-height:
    1.25;

  letter-spacing:
    -.35px;
}


.drawer-subtitle {
  margin:
    6px
    0
    0;

  color:
    #8d929a;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    11px;

  line-height:
    1.5;
}


/* ============================================================
   CLOSE
============================================================ */

.close-button {
  width:
    38px;

  height:
    38px;

  flex:
    0
    0
    38px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  box-sizing:
    border-box;

  padding:
    0;

  color:
    #68707b;

  background:
    rgba(35, 45, 65, .045);

  border:
    1px solid
    rgba(35, 45, 65, .055);

  border-radius:
    12px;

  cursor:
    pointer;

  transition:
    background-color .18s ease,
    transform .18s ease,
    opacity .18s ease;
}


.close-button svg {
  width:
    17px;

  height:
    17px;
}


.close-button:hover:not(:disabled) {
  background:
    rgba(35, 45, 65, .075);

  transform:
    scale(1.04);
}


.close-button:disabled {
  opacity:
    .35;

  cursor:
    not-allowed;
}


/* ============================================================
   BODY
============================================================ */

.drawer-body {
  position:
    relative;

  flex:
    1;

  min-height:
    0;

  box-sizing:
    border-box;

  padding:
    30px;

  overflow-y:
    auto;

  scrollbar-width:
    thin;

  scrollbar-color:
    rgba(120, 125, 135, .18)
    transparent;
}


/* ============================================================
   FORM CARD
============================================================ */

.form-content {
  width:
    100%;

  box-sizing:
    border-box;

  padding:
    26px;

  background:
    rgba(255, 255, 255, .88);

  border:
    1px solid
    rgba(31, 42, 60, .055);

  border-radius:
    22px;

  box-shadow:
    0
    12px
    34px
    rgba(30, 45, 70, .045);

  transition:
    opacity .2s ease,
    filter .2s ease;
}


.form-content--disabled {
  opacity:
    .5;

  filter:
    grayscale(.12);

  pointer-events:
    none;
}


/* ============================================================
   SECTIONS
============================================================ */

.form-section {
  display:
    flex;

  flex-direction:
    column;

  gap:
    18px;
}


.section-heading {
  display:
    flex;

  align-items:
    flex-start;

  gap:
    12px;
}


.section-number {
  width:
    31px;

  height:
    31px;

  flex:
    0
    0
    31px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #6c8db9;

  background:
    rgba(52, 127, 231, .07);

  border:
    1px solid
    rgba(52, 127, 231, .08);

  border-radius:
    9px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    8px;

  letter-spacing:
    .2px;
}


.section-title {
  margin:
    1px
    0
    0;

  color:
    #454b54;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    12px;

  line-height:
    1.35;
}


.section-description {
  margin:
    3px
    0
    0;

  color:
    #969ca5;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;

  line-height:
    1.45;
}


.section-divider {
  width:
    100%;

  height:
    1px;

  margin:
    25px
    0;

  background:
    rgba(35, 45, 60, .055);
}


/* ============================================================
   LANGUAGES
============================================================ */

.languages-grid {
  display:
    grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap:
    9px;
}


.language-card {
  min-height:
    52px;

  display:
    flex;

  align-items:
    center;

  gap:
    10px;

  box-sizing:
    border-box;

  padding:
    9px
    11px;

  color:
    #454b54;

  background:
    #f6f7f9;

  border:
    1px solid
    rgba(35, 45, 60, .045);

  border-radius:
    13px;

  text-align:
    left;

  cursor:
    pointer;

  transition:
    background-color .18s ease,
    border-color .18s ease,
    transform .18s ease,
    box-shadow .18s ease;
}


.language-card:hover:not(:disabled) {
  background:
    #f1f4f8;

  border-color:
    rgba(52, 127, 231, .10);

  transform:
    translateY(-1px);
}


.language-card.selected {
  background:
    rgba(52, 127, 231, .065);

  border-color:
    rgba(52, 127, 231, .22);

  box-shadow:
    0
    5px
    14px
    rgba(52, 127, 231, .055);
}


.language-icon {
  width:
    31px;

  height:
    31px;

  flex:
    0
    0
    31px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #7089aa;

  background:
    rgba(70, 95, 125, .055);

  border-radius:
    9px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    8px;

  letter-spacing:
    .3px;
}


.language-card.selected
.language-icon {
  color:
    #4c7dbb;

  background:
    rgba(52, 127, 231, .09);
}


.language-name {
  min-width:
    0;

  flex:
    1;

  overflow:
    hidden;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    10px;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;
}


.selection-mark {
  width:
    22px;

  height:
    22px;

  flex:
    0
    0
    22px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #347fe7;

  background:
    transparent;

  border:
    1px solid
    rgba(80, 90, 105, .10);

  border-radius:
    50%;
}


.language-card.selected
.selection-mark {
  color:
    #ffffff;

  background:
    #347fe7;

  border-color:
    #347fe7;
}


.selection-mark svg {
  width:
    14px;

  height:
    14px;
}


/* ============================================================
   LEVELS
============================================================ */

.levels-list {
  display:
    flex;

  flex-direction:
    column;

  gap:
    8px;
}


.level-card {
  min-height:
    61px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    space-between;

  gap:
    18px;

  box-sizing:
    border-box;

  padding:
    10px
    13px;

  color:
    #454b54;

  background:
    #f6f7f9;

  border:
    1px solid
    rgba(35, 45, 60, .045);

  border-radius:
    13px;

  text-align:
    left;

  cursor:
    pointer;

  transition:
    background-color .18s ease,
    border-color .18s ease,
    transform .18s ease,
    box-shadow .18s ease;
}


.level-card:hover:not(:disabled) {
  background:
    #f1f4f8;

  transform:
    translateY(-1px);
}


.level-card.selected {
  background:
    rgba(52, 127, 231, .06);

  border-color:
    rgba(52, 127, 231, .21);

  box-shadow:
    0
    5px
    14px
    rgba(52, 127, 231, .05);
}


.level-main {
  min-width:
    0;

  display:
    flex;

  align-items:
    center;

  gap:
    11px;
}


.level-indicator {
  width:
    18px;

  height:
    18px;

  flex:
    0
    0
    18px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  box-sizing:
    border-box;

  border:
    1.5px solid
    rgba(80, 90, 105, .22);

  border-radius:
    50%;

  transition:
    border-color .18s ease;
}


.level-indicator.selected {
  border-color:
    #347fe7;
}


.level-indicator-dot {
  width:
    8px;

  height:
    8px;

  background:
    #347fe7;

  border-radius:
    50%;

  opacity:
    0;

  transform:
    scale(.4);

  transition:
    opacity .18s ease,
    transform .18s ease;
}


.level-indicator.selected
.level-indicator-dot {
  opacity:
    1;

  transform:
    scale(1);
}


.level-copy {
  min-width:
    0;
}


.level-name {
  color:
    #4a5059;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.3;
}


.level-description {
  margin-top:
    3px;

  color:
    #969ca5;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    8px;

  line-height:
    1.4;
}


.level-bars {
  flex-shrink:
    0;

  display:
    flex;

  align-items:
    flex-end;

  gap:
    3px;
}


.level-bars span {
  width:
    4px;

  background:
    rgba(90, 100, 115, .12);

  border-radius:
    4px;
}


.level-bars span:nth-child(1) {
  height:
    7px;
}


.level-bars span:nth-child(2) {
  height:
    10px;
}


.level-bars span:nth-child(3) {
  height:
    13px;
}


.level-bars span:nth-child(4) {
  height:
    16px;
}


.level-bars span.filled {
  background:
    #80a8de;
}


/* ============================================================
   SUMMARY
============================================================ */

.selection-summary {
  margin-top:
    24px;

  display:
    flex;

  align-items:
    center;

  gap:
    10px;

  box-sizing:
    border-box;

  padding:
    11px
    12px;

  background:
    rgba(52, 127, 231, .045);

  border:
    1px solid
    rgba(52, 127, 231, .09);

  border-radius:
    13px;
}


.summary-icon {
  width:
    30px;

  height:
    30px;

  flex:
    0
    0
    30px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #5986bf;

  background:
    rgba(52, 127, 231, .075);

  border-radius:
    9px;
}


.summary-icon svg {
  width:
    17px;

  height:
    17px;
}


.summary-title {
  color:
    #60728a;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;
}


.summary-text {
  margin-top:
    2px;

  color:
    #8a929d;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;
}


.summary-text span {
  margin:
    0
    4px;

  color:
    #b2b7bd;
}


/* ============================================================
   ERROR
============================================================ */

.field-error {
  margin:
    0;

  color:
    #d95757;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.4;
}


/* ============================================================
   EMPTY
============================================================ */

.empty-languages {
  min-height:
    70px;

  display:
    flex;

  align-items:
    center;

  gap:
    11px;

  box-sizing:
    border-box;

  padding:
    13px;

  background:
    #f6f7f9;

  border:
    1px solid
    rgba(35, 45, 60, .045);

  border-radius:
    13px;
}


.empty-icon {
  width:
    34px;

  height:
    34px;

  flex:
    0
    0
    34px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #8994a3;

  background:
    rgba(70, 85, 105, .055);

  border-radius:
    10px;
}


.empty-icon svg {
  width:
    18px;

  height:
    18px;
}


.empty-title {
  color:
    #626973;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    10px;
}


.empty-description {
  margin-top:
    3px;

  color:
    #9a9fa7;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    8px;
}


/* ============================================================
   SAVING
============================================================ */

.saving-overlay {
  position:
    absolute;

  inset:
    0;

  z-index:
    10;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  padding:
    24px;

  background:
    rgba(250, 251, 252, .52);

  backdrop-filter:
    blur(4px);

  -webkit-backdrop-filter:
    blur(4px);
}


.saving-card {
  display:
    flex;

  align-items:
    center;

  gap:
    13px;

  padding:
    15px
    18px;

  background:
    rgba(255, 255, 255, .96);

  border:
    1px solid
    rgba(45, 60, 80, .07);

  border-radius:
    16px;

  box-shadow:
    0
    16px
    38px
    rgba(25, 40, 65, .12);
}


.saving-spinner {
  width:
    26px;

  height:
    26px;

  flex-shrink:
    0;

  box-sizing:
    border-box;

  border:
    3px solid
    rgba(52, 127, 231, .14);

  border-top-color:
    #347fe7;

  border-radius:
    50%;

  animation:
    spin
    .65s
    linear
    infinite;
}


.saving-text {
  display:
    flex;

  flex-direction:
    column;

  gap:
    3px;
}


.saving-text strong {
  color:
    #383d45;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    12px;
}


.saving-text span {
  color:
    #9399a2;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;
}


/* ============================================================
   FOOTER
============================================================ */

.drawer-footer {
  min-height:
    78px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    flex-end;

  gap:
    10px;

  box-sizing:
    border-box;

  padding:
    14px
    30px;

  background:
    rgba(255, 255, 255, .92);

  border-top:
    1px solid
    rgba(30, 40, 55, .06);

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);
}


/* ============================================================
   BUTTONS
============================================================ */

.cancel-button,
.save-btn {
  height:
    42px;

  box-sizing:
    border-box;

  padding:
    0
    20px;

  border:
    none;

  border-radius:
    12px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;

  cursor:
    pointer;

  transition:
    background-color .18s ease,
    color .18s ease,
    opacity .18s ease,
    box-shadow .18s ease,
    transform .18s ease;
}


.cancel-button {
  color:
    #5f6670;

  background:
    #f1f2f4;
}


.cancel-button:hover:not(:disabled) {
  background:
    #e9ebee;
}


.save-btn {
  min-width:
    132px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    7px;

  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #347fe7,
      #2769c7
    );

  box-shadow:
    0
    7px
    18px
    rgba(52, 127, 231, .18);
}


.save-btn svg {
  width:
    15px;

  height:
    15px;
}


.save-btn:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0
    9px
    22px
    rgba(52, 127, 231, .24);
}


.save-btn:disabled {
  color:
    #969ca4;

  background:
    #e1e3e7;

  box-shadow:
    none;

  cursor:
    not-allowed;
}


.cancel-button:disabled {
  opacity:
    .45;

  cursor:
    not-allowed;
}


/* ============================================================
   SPINNER
============================================================ */

.button-spinner {
  width:
    16px;

  height:
    16px;

  box-sizing:
    border-box;

  border:
    2px solid
    rgba(255, 255, 255, .36);

  border-top-color:
    #ffffff;

  border-radius:
    50%;

  animation:
    spin
    .65s
    linear
    infinite;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.drawer-overlay {
  background:
    rgba(0, 0, 0, .62);
}


html[data-theme="dark"]
.drawer-content {
  background:
    #1b1d21;

  border-right-color:
    rgba(255, 255, 255, .05);

  box-shadow:
    24px
    0
    70px
    rgba(0, 0, 0, .38);
}


html[data-theme="dark"]
.drawer-header {
  background:
    rgba(28, 30, 35, .92);

  border-bottom-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.drawer-title {
  color:
    #f0f1f3;
}


html[data-theme="dark"]
.drawer-subtitle {
  color:
    #858a93;
}


html[data-theme="dark"]
.close-button {
  color:
    #aeb3bb;

  background:
    rgba(255, 255, 255, .045);

  border-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.close-button:hover:not(:disabled) {
  background:
    rgba(255, 255, 255, .075);
}


html[data-theme="dark"]
.form-content {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, .065);

  box-shadow:
    0
    16px
    40px
    rgba(0, 0, 0, .18);
}


html[data-theme="dark"]
.section-number {
  color:
    #91b6ee;

  background:
    rgba(91, 158, 255, .07);

  border-color:
    rgba(91, 158, 255, .08);
}


html[data-theme="dark"]
.section-title {
  color:
    #d9dce1;
}


html[data-theme="dark"]
.section-description {
  color:
    #747981;
}


html[data-theme="dark"]
.section-divider {
  background:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.language-card,
html[data-theme="dark"]
.level-card,
html[data-theme="dark"]
.empty-languages {
  color:
    #d9dce1;

  background:
    #292b30;

  border-color:
    rgba(255, 255, 255, .045);
}


html[data-theme="dark"]
.language-card:hover:not(:disabled),
html[data-theme="dark"]
.level-card:hover:not(:disabled) {
  background:
    #2d3035;
}


html[data-theme="dark"]
.language-card.selected,
html[data-theme="dark"]
.level-card.selected {
  background:
    rgba(91, 158, 255, .065);

  border-color:
    rgba(91, 158, 255, .19);
}


html[data-theme="dark"]
.language-icon {
  color:
    #8798af;

  background:
    rgba(255, 255, 255, .04);
}


html[data-theme="dark"]
.language-card.selected
.language-icon {
  color:
    #91b6ee;

  background:
    rgba(91, 158, 255, .08);
}


html[data-theme="dark"]
.selection-mark {
  border-color:
    rgba(255, 255, 255, .10);
}


html[data-theme="dark"]
.language-card.selected
.selection-mark {
  color:
    #ffffff;

  background:
    #347fe7;

  border-color:
    #347fe7;
}


html[data-theme="dark"]
.level-name {
  color:
    #dfe1e5;
}


html[data-theme="dark"]
.level-description {
  color:
    #747981;
}


html[data-theme="dark"]
.level-indicator {
  border-color:
    rgba(255, 255, 255, .16);
}


html[data-theme="dark"]
.level-indicator.selected {
  border-color:
    #5b9eff;
}


html[data-theme="dark"]
.level-indicator-dot {
  background:
    #5b9eff;
}


html[data-theme="dark"]
.level-bars span {
  background:
    rgba(255, 255, 255, .08);
}


html[data-theme="dark"]
.level-bars span.filled {
  background:
    #749fd8;
}


html[data-theme="dark"]
.selection-summary {
  background:
    rgba(91, 158, 255, .055);

  border-color:
    rgba(91, 158, 255, .09);
}


html[data-theme="dark"]
.summary-icon {
  color:
    #91b6ee;

  background:
    rgba(91, 158, 255, .08);
}


html[data-theme="dark"]
.summary-title {
  color:
    #a9bee0;
}


html[data-theme="dark"]
.summary-text {
  color:
    #7f8791;
}


html[data-theme="dark"]
.field-error {
  color:
    #ff7c7c;
}


html[data-theme="dark"]
.empty-icon {
  color:
    #818994;

  background:
    rgba(255, 255, 255, .045);
}


html[data-theme="dark"]
.empty-title {
  color:
    #bdc1c7;
}


html[data-theme="dark"]
.empty-description {
  color:
    #747981;
}


html[data-theme="dark"]
.saving-overlay {
  background:
    rgba(20, 22, 26, .56);
}


html[data-theme="dark"]
.saving-card {
  background:
    #24272c;

  border-color:
    rgba(255, 255, 255, .07);

  box-shadow:
    0
    16px
    40px
    rgba(0, 0, 0, .34);
}


html[data-theme="dark"]
.saving-text strong {
  color:
    #eceef1;
}


html[data-theme="dark"]
.saving-text span {
  color:
    #858a93;
}


html[data-theme="dark"]
.drawer-footer {
  background:
    rgba(28, 30, 35, .94);

  border-top-color:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.cancel-button {
  color:
    #c5c8ce;

  background:
    #292c31;
}


html[data-theme="dark"]
.cancel-button:hover:not(:disabled) {
  background:
    #303339;
}


html[data-theme="dark"]
.save-btn:disabled {
  color:
    #727780;

  background:
    #303238;
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 640px) {

  .drawer-content {
    width:
      100%;
  }


  .drawer-header {
    min-height:
      82px;

    padding:
      17px
      18px;
  }


  .drawer-title {
    font-size:
      17px;
  }


  .drawer-subtitle {
    font-size:
      10px;
  }


  .close-button {
    width:
      34px;

    height:
      34px;

    flex-basis:
      34px;

    border-radius:
      10px;
  }


  .drawer-body {
    padding:
      18px;
  }


  .form-content {
    padding:
      20px;

    border-radius:
      18px;
  }


  .languages-grid {
    grid-template-columns:
      1fr;
  }


  .level-card {
    gap:
      10px;
  }


  .level-description {
    max-width:
      220px;
  }


  .drawer-footer {
    padding:
      12px
      18px;
  }


  .cancel-button {
    display:
      none;
  }


  .save-btn {
    width:
      100%;

    height:
      44px;
  }

}


/* ============================================================
   ANIMATIONS
============================================================ */

@keyframes spin {

  to {
    transform:
      rotate(360deg);
  }

}


.slide-left-enter-active,
.slide-left-leave-active {
  transition:
    transform
    .30s
    cubic-bezier(.22, 1, .36, 1),
    opacity
    .22s
    ease;
}


.slide-left-enter-from,
.slide-left-leave-to {
  transform:
    translateX(-100%);

  opacity:
    0;
}


.slide-left-enter-to,
.slide-left-leave-from {
  transform:
    translateX(0);

  opacity:
    1;
}


.fade-enter-active,
.fade-leave-active {
  transition:
    opacity
    .2s
    ease;
}


.fade-enter-from,
.fade-leave-to {
  opacity:
    0;
}


.summary-fade-enter-active,
.summary-fade-leave-active {
  transition:
    opacity .2s ease,
    transform .2s ease;
}


.summary-fade-enter-from,
.summary-fade-leave-to {
  opacity:
    0;

  transform:
    translateY(-4px);
}

</style>